import { useMemo } from "react"

import { CommentatorReaction } from "@/components/commentator-reaction"
import type { RecapCommentary, RecapCommentator } from "@/types"

interface CommentaryDeskProps {
  commentary: RecapCommentary[]
  commentatorMap: Record<string, RecapCommentator>
  portraitUrls: Record<string, string>
  opacities: number[]
  translateXs: number[]
  translateYs: number[]
  rotates: number[]
  scales: number[]
}

/**
 * Renders commentary as a conversation — items flow top-to-bottom in DOM order,
 * each pinned left or right based on commentator identity. First unique
 * commentator → left, second → right.
 */
export function CommentaryDesk({
  commentary,
  commentatorMap,
  portraitUrls,
  opacities,
  translateXs,
  translateYs,
  rotates,
  scales,
}: CommentaryDeskProps) {
  // Determine sides by commentator identity
  const items = useMemo(() => {
    const firstKey = commentary[0]?.commentator ?? null
    return commentary.map((line, idx) => {
      const side: "left" | "right" =
        line.commentator === firstKey ? "left" : "right"

      const commentator = commentatorMap[line.commentator]
      const portraitKey = line.mood ? `${line.commentator}_${line.mood}` : null
      const portraitUrl = portraitKey
        ? (portraitUrls[portraitKey] ?? null)
        : null

      return { line, idx, side, commentator, portraitUrl } as const
    })
  }, [commentary, commentatorMap, portraitUrls])

  return (
    <div className="flex flex-col gap-2">
      {items.map(({ line, idx, side, commentator, portraitUrl }) => (
        <div
          key={idx}
          style={{
            width: "90%",
            alignSelf: side === "left" ? "flex-start" : "flex-end",
            transform: `translateY(${translateYs[idx] ?? 0}px)`,
            willChange: "transform",
          }}
        >
          <CommentatorReaction
            commentatorKey={line.commentator}
            name={commentator?.name ?? line.commentator}
            mood={line.mood}
            line={line.line}
            portraitUrl={portraitUrl}
            side={side}
            opacity={opacities[idx] ?? 0}
            translateX={translateXs[idx] ?? (side === "left" ? -120 : 120)}
            rotate={rotates[idx] ?? 0}
            scale={scales[idx] ?? 0.5}
          />
        </div>
      ))}
    </div>
  )
}
